import { useTranslations } from 'next-intl';
import { FC } from 'react';
import { GoPlus } from 'react-icons/go';
import { LuMinus } from 'react-icons/lu';

import { useLocalizedProperty } from '@/hooks/useLocalizedProperty';
import { Link } from '@/i18n/routing';
import type { ICartItem } from '@/interfaces/cart.interface';
import SkeletonCartModal from '../ui/SkeletonCartModal';

type TCartItemProps = {
  item: ICartItem;
  onRemove: () => void;
  onQuantityChange: (newQuantity: number) => Promise<void>;
  isUpdating?: boolean;
};

const CartItem: FC<TCartItemProps> = ({ item, onRemove, onQuantityChange, isUpdating = false }) => {
  const t = useTranslations('cart');
  const getLocalizedProperty = useLocalizedProperty();
  const productName = item?.product?.name;
  const productSlug = item?.product?.slug;
  const productQuantity = item?.quantity;
  const productPrice = item?.variant?.price;
  const productImage = item?.product?.thumbnail?.s3url;

  const handleIncrease = async () => {
    if (isUpdating) return;
    const newQuantity = item.quantity + 1;
    await onQuantityChange(newQuantity);
  };

  const handleDecrease = async () => {
    if (isUpdating) return;
    const newQuantity = item.quantity - 1;
    await onQuantityChange(newQuantity);
  };

  return (
    <>
      {isUpdating ? (
        <SkeletonCartModal />
      ) : (
        <div className="mb-6 flex items-start border-b pb-4">
          <div className="mr-4">
            <img src={productImage} alt={'product-img'} className="max-h-[132px] max-w-[114px] object-cover" />
          </div>

          <div className="flex flex-1 flex-col">
            <div className="flex items-center justify-between text-base">
              <Link href={`/shop/${productSlug}`} className="w-40 truncate font-medium text-clr-0a dark:text-white">
                {getLocalizedProperty(productName)}
              </Link>
              <span className="font-semibold text-clr-0a dark:text-white">
                €{(productPrice * productQuantity).toFixed(2)}
              </span>
            </div>

            <div className="mt-2 flex flex-col justify-between">
              <div className="flex items-center text-sm text-clr-6A dark:text-clr-a4">
                <button onClick={handleDecrease}>
                  <LuMinus />
                </button>
                <span className="mx-2">
                  {t('cart_qty')}: {productQuantity}
                </span>
                <button onClick={handleIncrease}>
                  <GoPlus />
                </button>
              </div>
              <div className="mt-[46px] flex justify-between text-xs font-medium text-clr-6A dark:text-clr-a4">
                <button onClick={onRemove}>{t('cart_remove')}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CartItem;
